import type { Scene } from '@/types';

/**
 * Capítulo 5 — "El último fuego".
 *
 * Rework completo. Arco: convocatoria de las facciones en el Corazón
 * arrasado, asedio de Reyes con su ejército, activación (o no) de la
 * piedra según Cap 4, encrucijada con seis caminos a seis finales.
 *
 * Cada opción de la encrucijada tiene condiciones acumulativas: solo
 * ve las que pudo ganarse con lo que hizo.
 */
export const CAP5_ESCENAS: Scene[] = [
  // ===========================================================================
  //  ACTO I — La convocatoria
  // ===========================================================================
  {
    id: 'cap5_intro',
    capitulo: 5,
    titulo: 'El último fuego',
    background: { type: 'ruins', hint: 'Corazón de la Civilización quemado, tres días después' },
    musica: 'mystical_loop',
    texto: [
      'Tres días después estás otra vez en el Corazón. La ciudad que dejaste quemándose ya no está quemándose: está quemada. El fuego se comió lo que tenía que comer y se fue, como hacen los fuegos que saben que los dejan comer.',
      'Las columnas labradas con carpinchos bailando siguen de pie. La mayoría de los tejados cayeron. La biblioteca es un esqueleto de piedra. La cámara central, donde [if flag=cuervo_contenedor]dormía el Cuervo con la piedra adentro[/if][if flag=cuervo_sobrevive]el Cuervo había dormido a la piedra con dos hechizos ajenos[/if][if flag=cap4_sin_ritual]la piedra quedó sola, sin contenedor, sin custodia[/if], sigue cerrada. Nadie la abrió todavía.',
      '[if flag=pipo_rescatado]Pipo duerme atado a tu cadera. Lleva tres días durmiendo. Respirás dos veces por minuto, contás las dos. Si alguna se atrasa, te alarmás. Si no se atrasa, también.[/if]',
      'Todavía no llegó nadie más. Pero están en camino.',
    ],
    opciones: [
      {
        id: 'cap5_esperar',
        texto: 'Esperar. Que vengan.',
        destino: 'cap5_llegada',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap5_llegada',
    capitulo: 5,
    titulo: 'Cuatro caminos, uno solo',
    background: { type: 'ruins', hint: 'facciones convergiendo, plaza central del Corazón' },
    texto: [
      'Vienen por los cuatro caminos. Los Guardianes del Fuego bajan desde el oeste [if flag=karn_rescatado]con Orón al frente, Karn a su lado con una cojera que se va a quedar para toda la vida[/if][if flag=karn_no_rescatado]sin Orón. Un lugarteniente viejo los comanda. Orón se quedó en el puesto, curándose algo que no es la herida del cuerpo[/if], armados con lo que les dejó la última década.',
      'El Clan del Río sube por el sur. Sin fogón, sin tambores. Náyade a pie, cargando su propio bastón, caminando al paso al que caminan las viejas: como si el paso fuera una decisión, no una consecuencia. Detrás [if flag=mara_aliada]Mara con un rifle ajustado al hombro[/if][if flag=sena_vuelve_con_tebo]Sena con Tebo encima de los hombros[/if][if noflag=sena_vuelve_con_tebo]seis jóvenes a los que reconocés de las pasarelas[/if].',
      'Los Susurradores aparecen por arriba, por donde nadie esperaba: caminando por los filos de las crestas como lo hacen los que nunca usaron senderos. Vega ciega adelante, con una rama fina en la mano.',
      '[if flag=cuervo_sobrevive]El Cuervo, que llegó con vos desde la villa, se acomoda contra una columna. No habla. No necesita hablar todavía.[/if][if flag=cuervo_contenedor]El Cuervo no vino. Sigue durmiéndose con la piedra, algún día para siempre. Se extraña su voz seca, de un modo que no pensaste extrañar.[/if]',
      '[if flag=sena_al_sur]Por el sendero del sur, último, aparece Sena con Tebo y con una docena de carpinchos oscuros. La facción disidente de La Sombra aceptó la convocatoria. No son aliados todavía: son testigos.[/if]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'facciones_reunidas', valor: true },
      { kind: 'xp', cantidad: 60 },
    ],
    opciones: [
      {
        id: 'cap5_empezar_discurso',
        texto: 'Caminar al centro. No tenés discurso preparado. Vas a tener que improvisar.',
        destino: 'cap5_tensiones',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap5_tensiones',
    capitulo: 5,
    titulo: 'Lo que se traen encima',
    background: { type: 'ruins', hint: 'plaza central, facciones en semicírculo' },
    texto: [
      'Vos parado en el centro. Pipo atado a la cadera (o no). Todos te miran. Ninguno se habla entre sí: [if flag=karn_rescatado]los Guardianes del Fuego evitan la mirada del Clan del Río desde hace ocho generaciones[/if][if flag=karn_no_rescatado]sin Orón, los Guardianes están descoordinados y eso se ve en cómo se acomodan[/if]; los Susurradores miran en una dirección que no es ninguna; [if flag=sena_al_sur]los disidentes de La Sombra están del otro lado de la plaza, a diez metros del grupo que los hubiera llamado enemigos hace dos meses[/if][if noflag=sena_al_sur]no vino La Sombra. Ni la disidente ni la otra. Tizón está muerto y el Cuervo o está durmiendo o está acá, pero ninguno habla por ellos[/if].',
      'Náyade te mira. Es la primera en hablar, apenas.',
      '—Te dejamos a vos, Franco. Cada uno vino por sus razones. Si vas a decir algo, decilo. Si no, vamos a decidir sin hablar.',
    ],
    opciones: [
      {
        id: 'cap5_hablar_empatia',
        texto: 'Empezar por lo humano: nombrar a los muertos.',
        destino: 'cap5_discurso',
        intencion: 'empatico',
        efectos: [{ kind: 'stat', stat: 'empatia', delta: 2 }],
      },
      {
        id: 'cap5_hablar_estrategia',
        texto: 'Empezar por lo técnico: Reyes viene, la piedra está, faltan horas.',
        destino: 'cap5_discurso',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 2 }],
      },
      {
        id: 'cap5_hablar_valor',
        texto: 'Empezar por lo frontal: pelear o rendirse, no hay punto medio.',
        destino: 'cap5_discurso',
        intencion: 'valiente',
        efectos: [{ kind: 'stat', stat: 'valentia', delta: 2 }],
      },
    ],
  },

  {
    id: 'cap5_discurso',
    capitulo: 5,
    titulo: 'Lo que decís',
    background: { type: 'ruins', hint: 'Franco al centro, voces bajas después' },
    texto: [
      'Hablás. No mucho. Nunca fuiste de hablar mucho. Decís que Reyes viene, que el consorcio humano entendió tarde lo que La Sombra entendió temprano, que la piedra sigue ahí. Que ninguno de ustedes tiene margen para pelearse entre sí. Que las peleas entre las cuatro facciones son peleas viejas, con causas viejas, y que hoy ninguna de esas causas les va a servir.',
      'Cuando terminás, nadie aplaude. Nadie aplaude nunca en un sitio así. Pero Orón —si vino— te hace una cabeceada corta, seca. Vega —desde atrás— asiente sin mirarte. Náyade te apoya una mano en el hombro y te la saca enseguida.',
      'Los de La Sombra disidente se acercan dos pasos. Eso también es una respuesta.',
      'A lo lejos, del oeste, se levanta un polvo nuevo. Jinetes. Muchos.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'discurso_dado', valor: true },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'cap5_prepararse',
        texto: 'Prepararse. Reyes está al llegar.',
        destino: 'cap5_reyes_asedio',
        intencion: 'valiente',
      },
    ],
  },

  // ===========================================================================
  //  ACTO II — Reyes
  // ===========================================================================
  {
    id: 'cap5_reyes_asedio',
    capitulo: 5,
    titulo: 'Reyes vuelve',
    background: { type: 'fire', hint: 'polvo de cincuenta jinetes al oeste, cayendo en picada' },
    texto: [
      'Cincuenta hombres esta vez. Caballos. Rifles largos, de los que se cargan por atrás y disparan rápido. Dos carros con pólvora. Tres perros grandes, de esos que usan los cazadores del norte para rastrear personas por el sudor. Un oso de fuego nuevo, este con más cadenas que el anterior.',
      'Reyes al frente, impoluto, con un sombrero de ala ancha que no se le mueve. Lo reconocés incluso a cien metros: el tipo respira como si respirar fuera un trámite con proveedores.',
      '—No vinimos a negociar —grita, a cincuenta metros—. Ustedes tienen hasta que termine esta frase para tirar las armas y dejarnos llevar lo que vinimos a llevar. Termino.',
      'Nadie tira nada. El silencio dura un segundo. Después el mundo se vuelve ruido.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'reyes_llega_cap5', valor: true }],
    opciones: [
      {
        id: 'cap5_a_pelear',
        texto: 'Sacar la daga. Que sea lo que tenga que ser.',
        destino: 'cap5_batalla',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap5_batalla',
    capitulo: 5,
    titulo: 'La batalla',
    background: { type: 'fire', hint: 'caos de pólvora, carpinchos y humanos mezclados' },
    texto: [
      'No hay manera de narrar una pelea de cuatro facciones contra cincuenta hombres y volver entera. Te llegan imágenes. Náyade partiéndole la frente a un hombre con el bastón que no te imaginabas que podía partir nada. Orón —si vino— gritando órdenes en una frecuencia de garganta que no habías oído. Vega, ciega, esquivando balas como si el aire le chismeara las trayectorias. Sena disparando un rifle que no sabés de dónde sacó.',
      '[if flag=cuervo_sobrevive]El Cuervo apareciéndose al lado tuyo, inesperado, con una hoja curva larga, cubriéndote el costado izquierdo.[/if]',
      'Los perros caen. El oso cae después del tercer disparo de cañón del Clan del Río (no sabías que tenían cañón, aparentemente ni ellos sabían). Pero los rifles siguen. Los cuerpos se acumulan del lado de ustedes también. Esto no se gana. Esto se [emph]sobrevive[/emph].',
      'En el medio de todo, algo cambia.',
    ],
    onEnter: [
      { kind: 'stat', stat: 'vida', delta: -20 },
      { kind: 'flag', flagId: 'batalla_iniciada', valor: true },
      { kind: 'xp', cantidad: 100 },
    ],
    opciones: [
      {
        id: 'cap5_piedra_momento',
        texto: 'Mirar la cámara central. Algo pulsa.',
        destino: 'cap5_piedra_momento',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap5_piedra_momento',
    capitulo: 5,
    titulo: 'La piedra respira',
    background: { type: 'mystical', hint: 'cámara central pulsando con luz roja, desde afuera' },
    texto: [
      '[if flag=cuervo_contenedor]La cámara donde duerme el Cuervo-recipiente empieza a pulsar. Una luz roja sale por las rendijas de la puerta, cada cinco segundos, como un corazón viejo. No es que la piedra se despertó: es que la piedra está durmiéndose [emph]más profundo[/emph] porque afuera se está rompiendo el mundo y ella quiere alejarse del ruido. El Cuervo le está enseñando a dormir en plena tormenta. Ese es el último acto pedagógico de su vida.[/if]',
      '[if flag=ritual_susurrado_hecho]La cámara donde la piedra está dormida con hechizos ajenos empieza a vibrar. No se despierta del todo pero se mueve. La red de palabras que vos tejiste con Vega resiste. Le das más mana de lo que te queda: un tirón seco desde el pecho.[/if]',
      '[if flag=cap4_sin_ritual]La cámara estalla. La puerta de piedra se abre con un golpe seco que levanta polvo. Algo sale. No es la piedra: la piedra es el [shake]vehículo[/shake]. Lo que sale es la [emph]cosa[/emph] que estaba adentro de la piedra, que ahora busca recipiente.[/if]',
      '[if flag=pipo_rescatado]Pipo abre los ojos. Sus ojos vuelven a ser blancos, y esta vez no por cuatro segundos: [glow color=mystic]así se quedan[/glow]. Se desata solo de la bandolera. Camina hacia la cámara. No corre. Camina derecho, como quien va a una cita puntual. Nadie puede pararlo. Ni siquiera vos.[/if][if noflag=pipo_rescatado]En el otro extremo del valle —lejos, en territorio que ustedes no controlan— algo sucede que se siente en los pechos de todos. Pipo, dondequiera que esté, está yendo a encontrarse con su destino. Su destino no los esperó a ustedes.[/if]',
    ],
    onEnter: [
      { kind: 'stat', stat: 'mana', delta: -15 },
      { kind: 'flag', flagId: 'piedra_momento_critico', valor: true },
      { kind: 'xp', cantidad: 120 },
    ],
    opciones: [
      {
        id: 'cap5_a_encrucijada',
        texto: 'El mundo te pide una decisión. La última.',
        destino: 'cap5_encrucijada',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  ACTO III — La encrucijada
  // ===========================================================================
  {
    id: 'cap5_encrucijada',
    capitulo: 5,
    titulo: 'La encrucijada',
    background: { type: 'mystical', hint: 'tiempo suspendido en la batalla, Franco en el centro' },
    texto: [
      'Pasa una cosa rara que no vas a saber nunca cómo explicar. El tiempo no se detiene —eso ya está dicho en muchos libros—, el tiempo se [emph]abre[/emph]. Se vuelve ancho. La batalla sigue a tu alrededor en una lentitud que te permite mirar las cosas como si estuvieras adentro de un cuadro.',
      'Vega, desde diez metros, te mira como si pudiera mirar. —Este es el momento, Franco. Los que mirás pueden vivir o morir según lo que decidas. Vos también. Elegí con las cosas que tenés. No con las que te gustaría tener.',
      '[if flag=pipo_rescatado]Pipo, con sus ojos blancos, se detiene a la mitad del camino hacia la cámara. Está esperándote a [emph]vos[/emph]. Por alguna razón que la piedra quiere y la piedra sabe, la decisión la hacés vos.[/if]',
      'Abrís los brazos. Elegí.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'encrucijada_llegada', valor: true }],
    opciones: [
      {
        id: 'cap5_final_guardian',
        texto: '[emph]"No hay lados. Somos todos lo mismo. Pipo nos guía. Yo me quedo."[/emph]',
        destino: 'ending_guardian_1',
        intencion: 'empatico',
        condiciones: [
          { kind: 'reputation', facciónId: 'clan_rio', minimo: 30 },
          { kind: 'reputation', facciónId: 'guardianes_fuego', minimo: 20 },
          { kind: 'reputation', facciónId: 'susurradores', minimo: 20 },
          { kind: 'flag', flagId: 'pipo_rescatado' },
        ],
        alFallarCondicion: 'atenuar',
      },
      {
        id: 'cap5_final_renacimiento',
        texto: '[emph]"Salvamos lo que queda. Después voy al pueblo grande a avisarles qué está pasando."[/emph]',
        destino: 'ending_renacimiento_1',
        intencion: 'empatico',
        condiciones: [
          { kind: 'flag', flagId: 'pipo_rescatado' },
          { kind: 'reputation', facciónId: 'clan_rio', minimo: 20 },
        ],
        alFallarCondicion: 'atenuar',
      },
      {
        id: 'cap5_final_exilio',
        texto: '[emph]"No puedo salvarlos a todos. Me llevo a los que pueda. El resto queda a su suerte."[/emph]',
        destino: 'ending_exilio_1',
        intencion: 'neutral',
      },
      {
        id: 'cap5_final_corona',
        texto: '[emph]"La fuerza cierra esto. Yo gobierno lo que quede. Lo que no, desaparece."[/emph]',
        destino: 'ending_corona_1',
        intencion: 'hostil',
        condiciones: [
          { kind: 'reputation', facciónId: 'guardianes_fuego', minimo: 30 },
          { kind: 'stat', stat: 'valentia', minimo: 6 },
        ],
        alFallarCondicion: 'atenuar',
      },
      {
        id: 'cap5_final_sombra',
        texto: '[emph]"La Sombra tenía razón. Me sumo. Que la piedra haga lo suyo."[/emph]',
        destino: 'ending_sombra_1',
        intencion: 'hostil',
        condiciones: [
          { kind: 'flag', flagId: 'cuervo_sobrevive' },
          { kind: 'reputation', facciónId: 'sombra', minimo: -20 },
        ],
        alFallarCondicion: 'ocultar',
      },
      {
        id: 'cap5_final_ultimo',
        texto: '[emph]"No puedo. Ya está. Que se cierre."[/emph]',
        destino: 'ending_ultimo_fuego_1',
        intencion: 'neutral',
        condiciones: [
          { kind: 'flag', flagId: 'pipo_abandonado_inicio' },
          { kind: 'flag', flagId: 'rescate_cap1_fracasado' },
        ],
        alFallarCondicion: 'ocultar',
      },
    ],
  },
];
